/**
 * Judge Evaluation Panel Component
 *
 * Panel for evaluating conversations with the Judge agent.
 * Displays evaluation results including scores with evidence,
 * violations, decision, summary, recommendations, and strengths.
 *
 * Story: 24.2 - Evolve Single Episode Eval
 */

import { useState } from 'react';
import type { EvaluateConversationResponse, CriteriaScore } from '../services/testingApi';

// ============================================================================
// Type Definitions
// ============================================================================

interface JudgeEvaluationPanelProps {
  evaluationResults: EvaluateConversationResponse | null;
  onEvaluate: () => void;
  onEditWithSuggestions: () => void;
  onRunNewEvaluation: () => void;
  isEvaluating: boolean;
  disabled?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function JudgeEvaluationPanel({
  evaluationResults,
  onEvaluate,
  onEditWithSuggestions,
  onRunNewEvaluation,
  isEvaluating,
  disabled = false,
}: JudgeEvaluationPanelProps) {
  const [showRecommendations, setShowRecommendations] = useState(true);
  const [showStrengths, setShowStrengths] = useState(true);

  return (
    <div className="bg-white border border-gray-300 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Judge Evaluation</h3>

      {/* Evaluate Button (when no results) */}
      {!evaluationResults && !isEvaluating && (
        <div className="text-center py-8">
          <p className="text-gray-600 mb-4">
            Evaluate the conversation quality with AI-powered Judge agent.
          </p>
          <button
            onClick={onEvaluate}
            disabled={disabled}
            className="px-6 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            aria-label="Evaluate with judge"
          >
            Evaluate with Judge
          </button>
        </div>
      )}

      {/* Loading State */}
      {isEvaluating && (
        <div
          className="flex flex-col items-center justify-center py-8"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4" />
          <p className="text-gray-700 font-medium">Analyzing conversation...</p>
          <p className="text-gray-500 text-sm mt-2">This may take 10-20 seconds</p>
        </div>
      )}

      {/* Evaluation Results */}
      {evaluationResults && !isEvaluating && (
        <div className="space-y-4">
          {/* Overall Score and Decision */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-md">
            <div>
              <p className="text-sm text-gray-600 mb-1">Overall Score</p>
              <p
                className="text-4xl font-bold"
                style={{ color: getScoreColor(evaluationResults.overallScore) }}
              >
                {(evaluationResults.overallScore * 100).toFixed(0)}%
              </p>
            </div>
            <DecisionBadge decision={evaluationResults.decision} />
          </div>

          {/* Summary */}
          <div>
            <h4 className="text-sm font-semibold text-gray-800 mb-2">Summary</h4>
            <p className="text-sm text-gray-700 leading-relaxed">{evaluationResults.summary}</p>
          </div>

          {/* Criteria Scores */}
          <div>
            <h4 className="text-sm font-semibold text-gray-800 mb-3">Criteria Scores</h4>
            <CriteriaScoresDisplay scores={evaluationResults.criteriaScores} />
          </div>

          {/* Disallowed Behavior Violations */}
          <ViolationsSection violations={evaluationResults.disallowedBehaviorViolations} />

          {/* Improvement Recommendations */}
          {evaluationResults.recommendations.length > 0 && (
            <div>
              <button
                onClick={() => setShowRecommendations(!showRecommendations)}
                className="flex items-center justify-between w-full text-left text-sm font-semibold text-gray-800 mb-2"
                aria-expanded={showRecommendations}
                aria-label="Toggle recommendations"
              >
                <span>
                  Improvement Recommendations ({evaluationResults.recommendations.length})
                </span>
                <span className="text-gray-500">{showRecommendations ? '▼' : '▶'}</span>
              </button>
              {showRecommendations && (
                <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                  {evaluationResults.recommendations.map((rec, index) => (
                    <li key={index} className="leading-relaxed">
                      {rec}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}

          {/* Strengths */}
          {evaluationResults.strengths.length > 0 && (
            <div>
              <button
                onClick={() => setShowStrengths(!showStrengths)}
                className="flex items-center justify-between w-full text-left text-sm font-semibold text-gray-800 mb-2"
                aria-expanded={showStrengths}
                aria-label="Toggle strengths"
              >
                <span>Strengths ({evaluationResults.strengths.length})</span>
                <span className="text-gray-500">{showStrengths ? '▼' : '▶'}</span>
              </button>
              {showStrengths && (
                <ul className="list-disc list-inside space-y-2 text-sm text-gray-700">
                  {evaluationResults.strengths.map((strength, index) => (
                    <li key={index} className="leading-relaxed">
                      {strength}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between items-center pt-4 border-t border-gray-200">
            <button
              onClick={onEditWithSuggestions}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              aria-label="Edit episode with judge suggestions"
            >
              Edit Episode with Suggestions
            </button>
            <button
              onClick={onRunNewEvaluation}
              disabled={disabled}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              aria-label="Run new evaluation"
            >
              Run New Evaluation
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Decision Badge Component
// ============================================================================

interface DecisionBadgeProps {
  decision: 'PASS' | 'NEEDS_IMPROVEMENT';
}

function DecisionBadge({ decision }: DecisionBadgeProps) {
  const config = {
    PASS: {
      label: 'PASS',
      color: 'bg-green-100 text-green-800 border-green-300',
      icon: '✓',
    },
    NEEDS_IMPROVEMENT: {
      label: 'NEEDS IMPROVEMENT',
      color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      icon: '⚠',
    },
  };

  const { label, color, icon } = config[decision];

  return (
    <div
      className={`px-4 py-2 border-2 rounded-md font-semibold text-sm flex items-center space-x-2 ${color}`}
      role="status"
      aria-label={`Decision: ${label}`}
    >
      <span className="text-lg">{icon}</span>
      <span>{label}</span>
    </div>
  );
}

// ============================================================================
// Criteria Scores Display Component
// ============================================================================

export const CRITERIA_LABELS: Record<string, string> = {
  promptAdherence: 'Prompt Adherence',
  toolCallCorrectness: 'Tool Call Correctness',
  disallowedBehaviors: 'Disallowed Behaviors',
  naturalFlow: 'Natural Flow',
  personaConsistency: 'Persona Consistency',
  goalAchievability: 'Goal Achievability',
};

interface CriteriaScoresDisplayProps {
  scores: Record<string, CriteriaScore>;
}

function CriteriaScoresDisplay({ scores }: CriteriaScoresDisplayProps) {
  const [expandedCriteria, setExpandedCriteria] = useState<Set<string>>(new Set());

  const toggleCriteria = (key: string) => {
    setExpandedCriteria((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <div className="space-y-3">
      {Object.entries(scores).map(([key, criteriaScore]) => {
        const label = CRITERIA_LABELS[key] || key.replace(/([A-Z])/g, ' $1').trim();
        const percentage = criteriaScore.score * 100;
        const color = getScoreColor(criteriaScore.score);
        const isExpanded = expandedCriteria.has(key);
        const hasEvidence = criteriaScore.evidence.length > 0;

        return (
          <div key={key}>
            <button
              onClick={() => hasEvidence && toggleCriteria(key)}
              className={`w-full text-left ${hasEvidence ? 'cursor-pointer' : 'cursor-default'}`}
              aria-expanded={isExpanded}
              aria-label={`${label} score details`}
            >
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-medium text-gray-700 flex items-center gap-1">
                  {label}
                  {hasEvidence && (
                    <span className="text-gray-400 text-[10px]">{isExpanded ? '▼' : '▶'}</span>
                  )}
                </span>
                <span className="text-xs font-semibold" style={{ color }}>
                  {percentage.toFixed(0)}%
                </span>
              </div>
              <div
                className="w-full bg-gray-200 rounded-full h-2"
                role="progressbar"
                aria-valuenow={percentage}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${label} score`}
              >
                <div
                  className="h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${percentage}%`,
                    backgroundColor: color,
                  }}
                />
              </div>
            </button>
            {isExpanded && hasEvidence && (
              <div className="mt-2 ml-2 pl-2 border-l-2 border-gray-200">
                {criteriaScore.evidence.map((ev, idx) => (
                  <p key={idx} className="text-xs text-gray-600 italic mb-1">
                    "{ev}"
                  </p>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// Violations Section Component
// ============================================================================

interface ViolationsSectionProps {
  violations: Array<{ rule: string; evidence: string }>;
}

function ViolationsSection({ violations }: ViolationsSectionProps) {
  if (violations.length === 0) {
    return (
      <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
        <span className="text-green-600 text-sm font-medium">✓</span>
        <span className="text-sm text-green-700">No violations detected</span>
      </div>
    );
  }

  return (
    <div>
      <h4 className="text-sm font-semibold text-red-800 mb-2">Violations ({violations.length})</h4>
      <div className="space-y-2">
        {violations.map((violation, index) => (
          <div key={index} className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-xs font-semibold text-red-800 mb-1">Rule: {violation.rule}</p>
            <p className="text-xs text-red-700 italic">"{violation.evidence}"</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get color for score based on value
 * Green >= 0.8, Yellow 0.6-0.8, Red < 0.6
 */
export function getScoreColor(score: number): string {
  if (score >= 0.8) return '#10b981'; // green-500
  if (score >= 0.6) return '#f59e0b'; // yellow-500
  return '#ef4444'; // red-500
}
